import { Box, FormHelperText } from '@mui/material';
import { useState, useEffect } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { InvoiceArticle } from 'src/modules/invoice/models/invoiceArticle';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getNestedFormErrors } from 'src/shared/functions/getNestedFormErrors';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllInvoiceArticle } from 'src/shared/services/invoiceArticleService';

type CreateInvoiceArticlePickerProps = {
  name: string;
  label: string;
  textInputReference: string;
  initialValue: string | undefined;
  index: number;
};

interface DisplayInvoiceArticle extends InvoiceArticle {
  pickerText: string;
}

export const CreateInvoiceArticlePicker = (props: CreateInvoiceArticlePickerProps) => {
  const { name, label, textInputReference, initialValue, index } = props;
  const isMounted = useIsMounted();
  const { setValue, trigger } = useFormContext();
  const { errors } = useFormState();

  const [, setLoading] = useState<boolean>(true);
  const [invoiceArticles, setInvoiceArticles] = useState<DisplayInvoiceArticle[]>();
  const [articleError, setArticleError] = useState<string>();
  const [articleNr, setArticleNr] = useState<string>();

  useEffect(() => {
    const errorMessage = getNestedFormErrors(errors, name);
    setArticleError(errorMessage);
  }, [errors, name]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getAllInvoiceArticle({ pageIndex: 0, pageSize: 500, sortCol: 'name', sortDir: 'asc', filter: '' });
        if (isMounted()) {
          setInvoiceArticles(
            result.data.results.map((item) => {
              return { ...item, pickerText: `${item.articleNr} - ${item.name}` };
            }),
          );
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [isMounted]);

  const update = (val: InvoiceArticle | null) => {
    setValue(name, val?.articleNr);
    setValue(textInputReference, val?.name);
    setArticleNr(val?.articleNr || '');
    setValue(`rows.${index}.amount`, val?.price || 0);
    trigger();
  };

  useEffect(() => {
    if (invoiceArticles?.length && initialValue) setArticleNr(initialValue);
  }, [initialValue, invoiceArticles?.length]);

  return (
    <>
      <Box sx={{ display: 'none' }}>
        <ControlledTextField name={name} />
      </Box>
      <AutocompleteDropdown<InvoiceArticle>
        options={invoiceArticles || []}
        nameProp='pickerText'
        keyProp='article_id'
        name='articlePicker'
        value={invoiceArticles ? invoiceArticles.filter((x) => x.articleNr === articleNr)[0] : undefined}
        emptyLabel='invoice:createInvoce.selectArticle'
        label={label}
        error={!!articleError}
        callback={(val) => {
          update(val);
        }}
        componentWidth='100%'
      />
      {articleError && (
        <FormHelperText error sx={{ pl: 1 }}>
          <em>{articleError}</em>
        </FormHelperText>
      )}
    </>
  );
};
